import {
  formatDatePart,
  isDateTodayOrTomorrow,
  getPeriodLabel,
  formatTime12h,
  dateDiffDays,
} from '../../utils/parseStartDate'
import type { DeadlineValue } from './DeadlineDropdown.types'

export function formatDeadlineDate(
  value: DeadlineValue,
  locale: string = 'de',
  referenceDate?: Date,
): string {
  const ref = referenceDate ?? new Date()
  const isDE = locale.startsWith('de')
  const datePart = formatDatePart(value.date, ref, isDE, true)

  if (value.period) {
    return `${datePart} ${getPeriodLabel(value.period, isDE)}`
  }

  if (value.time) {
    const separator = isDateTodayOrTomorrow(value.date, ref) ? ' ' : ', '
    if (isDE) {
      return `${datePart}${separator}${value.time}`
    }
    return `${datePart}${separator}${formatTime12h(value.time)}`
  }

  return datePart
}

export function formatDeadlineBadge(
  value: DeadlineValue,
  locale: string = 'de',
  referenceDate?: Date,
): string {
  return formatDatePart(value.date, referenceDate ?? new Date(), locale.startsWith('de'), true)
}

/**
 * Format deadline as relative days for the collapsed task row badge.
 *
 * - 0 / 1 days:          "Heute" / "Morgen" (with optional period/time suffix)
 * - 2–14 days:           "in X Tagen" / "in X days"
 * - 15–56 days (≤8 wk):  "In X Wochen" / "In X weeks"
 * - > 56 days (>8 wk):   short date, e.g. "19. Sep" / "Sep 19"
 *
 * @example formatDeadlineRelativeBadge({ date: today }, 'de')                       → "Heute"
 * @example formatDeadlineRelativeBadge({ date: today, period: 'evening' }, 'de')    → "Heute Abend"
 * @example formatDeadlineRelativeBadge({ date: today, time: '14:30' }, 'de')        → "Heute 14:30"
 * @example formatDeadlineRelativeBadge({ date: tomorrow, period: 'noon' }, 'de')    → "Morgen Mittag"
 * @example formatDeadlineRelativeBadge({ date: in9days }, 'de')                     → "in 9 Tagen"
 * @example formatDeadlineRelativeBadge({ date: in15days }, 'de')                    → "In 2 Wochen"
 * @example formatDeadlineRelativeBadge({ date: in60days }, 'de')                    → "19. Sep"
 * @example formatDeadlineRelativeBadge({ date: yesterday }, 'de')                   → "Gestern fällig"
 * @example formatDeadlineRelativeBadge({ date: 5daysAgo }, 'de')                    → "Vor 5 Tagen fällig"
 */
export function formatDeadlineRelativeBadge(
  value: DeadlineValue,
  locale: string = 'de',
  referenceDate?: Date,
): string {
  const ref = referenceDate ?? new Date()
  const diff = dateDiffDays(value.date, ref)
  const isDE = locale.startsWith('de')

  // Optional suffix for Heute / Morgen: period label (e.g. "Abend") or formatted time.
  const dayPartSuffix = (): string => {
    if (value.period) return ` ${getPeriodLabel(value.period, isDE)}`
    if (value.time) return isDE ? ` ${value.time}` : ` ${formatTime12h(value.time)}`
    return ''
  }

  if (diff === 0) return (isDE ? 'Heute' : 'Today') + dayPartSuffix()
  if (diff === 1) return (isDE ? 'Morgen' : 'Tomorrow') + dayPartSuffix()
  if (diff >= 2 && diff <= 14) return isDE ? `in ${diff} Tagen` : `in ${diff} days`
  if (diff >= 15 && diff <= 56) {
    const weeks = Math.round(diff / 7)
    return isDE ? `In ${weeks} Wochen` : `In ${weeks} weeks`
  }
  if (diff > 56) {
    const target = new Date(value.date)
    target.setHours(0, 0, 0, 0)
    if (isDE) {
      const day = target.getDate()
      const month = target.toLocaleDateString('de-DE', { month: 'short' }).replace(/\.$/, '')
      return `${day}. ${month}`
    }
    return target.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  if (diff === -1) return isDE ? 'Gestern fällig' : 'Due yesterday'
  const absDiff = -diff
  if (absDiff >= 7) {
    const weeks = Math.floor(absDiff / 7)
    return isDE ? `Vor ${weeks} Wo. fällig` : `Due ${weeks}w ago`
  }
  return isDE ? `Vor ${absDiff} Tagen fällig` : `Due ${absDiff} days ago`
}
